import React, { createContext, useState, useCallback, useContext } from 'react';

/**
 * WorkspaceContext tracks which workspace is connected via WebSocket.
 *
 * When a workspace is selected:
 * 1. User selects workspace in UI
 * 2. ChatInterface component creates WebSocket connection
 * 3. WebSocket connect message triggers workspace initialization on backend
 * 4. ChatInterface notifies WorkspaceContext when WebSocket is connected
 * 5. Other components can check if workspace is "connected" before making API calls
 *
 * This simple approach prevents API calls during the brief moment when a workspace
 * is selected but the WebSocket hasn't connected yet (and workspace files are
 * being initialized from S3).
 */

const WorkspaceContext = createContext();

export const WorkspaceProvider = ({ children }) => {
  // Set of workspace IDs that are currently connected via WebSocket
  const [connectedWorkspaces, setConnectedWorkspaces] = useState(new Set());

  /**
   * Check if a workspace is connected and ready for API calls
   * Workspace is ready only after WebSocket connection is established
   */
  const isWorkspaceConnected = useCallback((workspaceId) => {
    return workspaceId && connectedWorkspaces.has(workspaceId);
  }, [connectedWorkspaces]);

  /**
   * Mark a workspace as connected
   * Called by ChatInterface when WebSocket connection is established
   */
  const markConnected = useCallback((workspaceId) => {
    setConnectedWorkspaces(prev => {
      const updated = new Set(prev);
      updated.add(workspaceId);
      return updated;
    });
  }, []);

  /**
   * Mark a workspace as disconnected
   * Called by ChatInterface when WebSocket connection closes
   */
  const markDisconnected = useCallback((workspaceId) => {
    setConnectedWorkspaces(prev => {
      const updated = new Set(prev);
      updated.delete(workspaceId);
      return updated;
    });
  }, []);

  const value = {
    isWorkspaceConnected,
    markConnected,
    markDisconnected
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

/**
 * Hook to use workspace context
 * Throws error if used outside of WorkspaceProvider
 */
export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
};
